<script setup lang="ts">
/*
  * - Root vue component of stage-ui-three package
  * - This scene component the root for all the sub components in the 3d scene
  * - This package, stage-ui-three, is a stateful package
  * - Pinia store is used to store the data/configuration of the model, camera, lighting, etc.
  * - Src of model is obtained from stage-ui via props, which is NOT a part of stage-ui-three package
*/

import type { VRM } from '@pixiv/three-vrm'
import type { TresContext } from '@tresjs/core'
import type { DirectionalLight, SphericalHarmonics3, Texture, WebGLRenderer, WebGLRenderTarget } from 'three'

import type { SceneBootstrap, ScenePhase, Vec3 } from '../stores/model-store'
import type { VrmLifecycleReason } from '../trace'

import { Screen } from '@proj-airi/ui'
import { TresCanvas } from '@tresjs/core'
import { EffectComposerPmndrs, HueSaturationPmndrs } from '@tresjs/post-processing'
import { formatHex } from 'culori'
import { storeToRefs } from 'pinia'
import { BlendFunction } from 'postprocessing'
import {
  ACESFilmicToneMapping,
  Euler,
  MathUtils,
  PerspectiveCamera,
  Vector3,
} from 'three'
import { computed, nextTick, onMounted, onUnmounted, ref, shallowRef, watch } from 'vue'

import { animations } from '../assets/vrm/animations'
// From stage-ui-three package
import { useRenderTargetRegionAtClientPoint } from '../composables/render-target'
// pinia store
import { useAnimationsStore } from '../stores/animations-store'
import { useModelStore } from '../stores/model-store'
import { defaultControlConfig } from '../stores/view-control'
import {
  getStageThreeRuntimeTraceContext,
  isStageThreeRuntimeTraceEnabled,
  stageThreeTraceRenderInfoEvent,
  stageThreeTraceThreeSceneComponentStateEvent,
  stageThreeTraceThreeSceneMutationLockEvent,
  stageThreeTraceThreeScenePhaseEvent,
  stageThreeTraceThreeSceneSubtreeEvent,
  stageThreeTraceThreeSceneTransactionEvent,
} from '../trace'
import { OrbitControls } from './Controls'
import { SkyBox } from './Environment'
import { VRMModel } from './Model'

const props = withDefaults(defineProps<{
  currentAudioSource?: AudioBufferSourceNode
  cursorPosition?: { x: number, y: number }
  modelSrc?: string
  skyBoxSrc?: string
  /** API服务器基础URL，用于加载动画 */
  apiBaseUrl?: string
  /** 是否在页面加载时刷新动画库 */
  refreshAnimationsOnMount?: boolean
  /**
   * Enables user-driven OrbitControls camera rotation and zoom.
   *
   * This is controlled by the app shell because orbit gestures conflict with
   * mobile view-control overlays, while desktop/tamagotchi stages still expect
   * direct scene manipulation.
   *
   * | Surface | Expected value | Reason |
   * | --- | --- | --- |
   * | stage-web desktop | `true` | Mouse orbit is the primary camera control. |
   * | stage-web mobile | `true` | Touch rotate + pinch-to-zoom for playing with the model. |
   * | stage-pocket mobile | `true` | Touch rotate + pinch-to-zoom + pan, see `enableOrbitPan`. |
   * | stage-tamagotchi | `true` | The transparent desktop stage has no mobile view-control overlay. |
   */
  enableOrbitControls?: boolean
  /**
   * Additionally allow panning (two-finger drag on touch, right-drag on mouse) while
   * `enableOrbitControls` is on. Off by default so surfaces that only want
   * rotate/zoom keep their current behaviour.
   */
  enableOrbitPan?: boolean
  showAxes?: boolean
  /** 动画阶段：greet（欢迎）或 idle（待机） */
  animationPhase?: 'greet' | 'idle'
  idleAnimation?: string
  // Ordered list of .vrma URLs the model cycles through on the web stage,
  // switching every 20s with a cross-fade. When non-empty, it takes precedence over `idleAnimation`.
  // 如果不提供，将从服务器动态获取idle动画列表
  idleAnimationPlaylist?: string[]
  paused?: boolean
}>(), {
  enableOrbitControls: true,
  enableOrbitPan: false,
  showAxes: false,
  animationPhase: 'greet', // 默认从greet开始
  refreshAnimationsOnMount: true,
  paused: false,
})

const emit = defineEmits<{
  (e: 'loadModelProgress', value: number): void
  (e: 'error', value: unknown): void
  (e: 'animationComplete', phase: 'greet' | 'idle'): void
}>()

type ModelPhase = 'no-model' | 'loading' | 'ready' | 'error'
type SceneTracePhaseCause
  = | 'binding:complete'
    | 'binding:start'
    | 'component:unmount'
    | 'controls-ready'
    | 'controls-ref:detached'
    | 'model-ref:detached'
    | 'props:model-src'
    | 'tres:ready'
    | 'vrm:error'
    | 'vrm:load-start'
    | 'vrm:loaded'
type SceneTraceTransactionReason = 'component-unmount' | 'initial-load' | 'model-reload' | 'model-switch' | 'no-model' | 'subtree-remount' | 'unknown'

const componentState = defineModel<'pending' | 'loading' | 'mounted'>('state', { default: 'pending' })

const modelStore = useModelStore()
const animationsStore = useAnimationsStore()

const {
  beginSceneBindingTransaction,
  endSceneBindingTransaction,
  resetSceneBindingTransactions,
  setScenePhase,
} = modelStore
const {
  sceneMutationLocked,
  scenePhase,
  sceneTransactionDepth,

  lastCommittedModelSrc,
  modelSize,
  modelOrigin,
  modelOffset,
  modelRotationY,

  cameraPosition,
  cameraDistance,

  directionalLightPosition,
  directionalLightTarget,
  directionalLightRotation,
  directionalLightIntensity,
  directionalLightColor,

  ambientLightIntensity,
  ambientLightColor,

  hemisphereSkyColor,
  hemisphereGroundColor,
  hemisphereLightIntensity,

  lookAtTarget,
  trackingMode,
  eyeHeight,
  envSelect,
  skyBoxSrc,
  skyBoxIntensity,
  renderScale,
  multisampling,
} = storeToRefs(modelStore)

// 动画状态管理
const {
  isLoaded: animationsLoaded,
} = storeToRefs(animationsStore)

/**
 * 从模型URL解析角色性别
 * 如果URL包含 '/female/' 则返回 'female'，包含 '/male/' 则返回 'male'
 * 默认返回 'female'
 */
const characterGender = computed((): 'female' | 'male' => {
  if (!props.modelSrc)
    return 'female'

  if (props.modelSrc.includes('/female/'))
    return 'female'

  if (props.modelSrc.includes('/male/'))
    return 'male'

  return 'female'
})

// 当前动画阶段（用于传递给VRMModel）
const currentAnimationPhase = ref<'greet' | 'idle'>(props.animationPhase || 'greet')

// 默认动画URL（本地fallback）
// 使用assets中存在的动画文件
const defaultIdleAnimationUrls = [animations.fCommon.peaceSign.href]

// 动画URL列表（从服务器动态获取，或使用本地默认）
const greetAnimationUrl = computed(() => {
  // 如果动画库未初始化，暂时返回undefined
  if (!animationsInitialized.value) {
    return undefined
  }

  const gender = characterGender.value
  const greetAnims = animationsStore.getGreetAnimations(gender)

  if (!props.apiBaseUrl || !animationsLoaded.value || greetAnims.length === 0) {
    // 没有apiBaseUrl或动画库未加载，使用undefined（会fallback到idle动画）
    return undefined
  }

  // 随机选择一个greet动画
  const randomGreet = animationsStore.selectRandomGreet(gender)
  console.log('[ThreeScene] Greet animation selected:', randomGreet?.url)
  return randomGreet ? animationsStore.resolveAnimationUrl(props.apiBaseUrl, randomGreet) : undefined
})

const idleAnimationUrls = computed(() => {
  // 如果动画库未初始化，暂时使用本地默认动画
  if (!animationsInitialized.value) {
    console.log('[ThreeScene] Using fallback animations (not initialized)')
    return defaultIdleAnimationUrls
  }

  // 如果有用户提供的动画列表，优先使用
  if (props.idleAnimationPlaylist?.length) {
    console.log('[ThreeScene] Using user-provided playlist')
    return props.idleAnimationPlaylist
  }

  // 如果有单个idle动画props，使用它
  if (props.idleAnimation) {
    console.log('[ThreeScene] Using user-provided idle animation')
    return [props.idleAnimation]
  }

  // 如果有apiBaseUrl且动画库已加载，使用服务器动态动画
  const gender = characterGender.value
  const idleAnims = animationsStore.getIdleAnimations(gender)

  if (props.apiBaseUrl && animationsLoaded.value && idleAnims.length > 0) {
    const urls = idleAnims.map(anim => animationsStore.resolveAnimationUrl(props.apiBaseUrl!, anim))
    console.log('[ThreeScene] Using server animations:', urls.length, 'for gender:', gender)
    return urls
  }

  // fallback：使用本地默认动画
  console.log('[ThreeScene] Using fallback animations')
  return defaultIdleAnimationUrls
})

type VrmFrameRuntimeHook = (vrm: VRM, delta: number) => void

const modelRef = ref<InstanceType<typeof VRMModel>>()
const vrmFrameRuntimeHook = shallowRef<VrmFrameRuntimeHook>()

const camera = shallowRef(new PerspectiveCamera())
const controlsRef = shallowRef<InstanceType<typeof OrbitControls>>()
const tresContextRef = shallowRef<TresContext>()
const screenRef = ref<InstanceType<typeof Screen>>()
const skyBoxEnvRef = ref<InstanceType<typeof SkyBox>>()
const dirLightRef = ref<InstanceType<typeof DirectionalLight>>()
const stageThreeRuntimeTraceContext = getStageThreeRuntimeTraceContext()
const stageThreeSceneTraceOriginId = `three-scene:${Math.random().toString(36).slice(2, 10)}`
const latestScenePhaseTraceCause = ref<SceneTracePhaseCause>('props:model-src')
const latestSceneTransactionReason = ref<SceneTraceTransactionReason>('unknown')
const activeModelSrc = ref<string>()
const bindingRevision = ref(0)
const pendingCommittedModelSrc = ref<string>()
const pendingCommittedModelRevision = ref<number>()
const pendingSceneBootstrap = shallowRef<SceneBootstrap>()

function emitThreeSceneTrace(label: string, event: any, payload: Record<string, unknown>) {
  if (isStageThreeRuntimeTraceEnabled())
    stageThreeRuntimeTraceContext.emit(event, payload)
}

function emitScenePhaseTrace(cause: SceneTracePhaseCause, from: ScenePhase | undefined, to: ScenePhase) {
  emitThreeSceneTrace('phase', stageThreeTraceThreeScenePhaseEvent, {
    cause,
    from,
    modelSrc: props.modelSrc,
    originId: stageThreeSceneTraceOriginId,
    to,
    transactionDepth: sceneTransactionDepth.value,
    ts: performance.now(),
  })
}

function emitSceneSubtreeTrace(key: 'controlsRef' | 'dirLightRef' | 'modelRef' | 'tresCanvasRef', action: 'attached' | 'detached') {
  emitThreeSceneTrace('subtree', stageThreeTraceThreeSceneSubtreeEvent, {
    action,
    key,
    originId: stageThreeSceneTraceOriginId,
    scenePhase: scenePhase.value,
    transactionDepth: sceneTransactionDepth.value,
    ts: performance.now(),
  })
}

function emitSceneTransactionTrace(action: 'begin' | 'end' | 'reset', reason: SceneTraceTransactionReason) {
  emitThreeSceneTrace('transaction', stageThreeTraceThreeSceneTransactionEvent, {
    action,
    depth: sceneTransactionDepth.value,
    originId: stageThreeSceneTraceOriginId,
    reason,
    scenePhase: scenePhase.value,
    ts: performance.now(),
  })
}

function emitSceneMutationLockTrace(locked: boolean) {
  emitThreeSceneTrace('mutation-lock', stageThreeTraceThreeSceneMutationLockEvent, {
    locked,
    originId: stageThreeSceneTraceOriginId,
    scenePhase: scenePhase.value,
    transactionDepth: sceneTransactionDepth.value,
    ts: performance.now(),
  })
}

function emitSceneComponentStateTrace(
  from: 'pending' | 'loading' | 'mounted' | undefined,
  to: 'pending' | 'loading' | 'mounted',
  diagnostics: {
    canvasReady: boolean
    controlsReady: boolean
    modelPhase: ModelPhase
  },
) {
  emitThreeSceneTrace('component-state', stageThreeTraceThreeSceneComponentStateEvent, {
    canvasReady: diagnostics.canvasReady,
    controlsReady: diagnostics.controlsReady,
    from,
    modelPhase: diagnostics.modelPhase,
    originId: stageThreeSceneTraceOriginId,
    scenePhase: scenePhase.value,
    to,
    transactionDepth: sceneTransactionDepth.value,
    ts: performance.now(),
  })
}
function toVector3(value: Vec3) {
  return new Vector3(value.x, value.y, value.z)
}

function toVec3(value: Vector3): Vec3 {
  return { x: value.x, y: value.y, z: value.z }
}

function clearPendingCommittedModel() {
  pendingCommittedModelSrc.value = undefined
  pendingCommittedModelRevision.value = undefined
}

function invalidateBindingRevision() {
  bindingRevision.value += 1
  clearPendingCommittedModel()
}

function applySceneBootstrap(value: SceneBootstrap) {
  const reason = latestSceneTransactionReason.value
  const previousOrigin = toVector3(modelOrigin.value)
  const previousCameraOffset = toVector3(cameraPosition.value).sub(previousOrigin)
  const previousTargetOffset = toVector3(lookAtTarget.value).sub(previousOrigin)
  const nextOrigin = toVector3(value.modelOrigin)
  const bootstrapCameraOffset = toVector3(value.cameraPosition).sub(nextOrigin)

  modelOrigin.value = { ...value.modelOrigin }
  modelSize.value = { ...value.modelSize }
  eyeHeight.value = value.eyeHeight

  if (reason === 'initial-load' || reason === 'unknown' || reason === 'no-model' || reason === 'model-switch') {
    modelOffset.value = {
      x: defaultControlConfig.x.default,
      y: value.modelOffset.y,
      z: value.modelOffset.z,
    }
    cameraDistance.value = value.cameraDistance
    cameraPosition.value = { ...value.cameraPosition }
    lookAtTarget.value = { ...value.lookAtTarget }
    return
  }

  const effectiveCameraDistance = cameraDistance.value > 1e-6 ? cameraDistance.value : value.cameraDistance
  cameraDistance.value = effectiveCameraDistance

  const nextCameraDirection = previousCameraOffset.lengthSq() > 1e-6
    ? previousCameraOffset.normalize()
    : bootstrapCameraOffset.lengthSq() > 1e-6
      ? bootstrapCameraOffset.normalize()
      : new Vector3(0, 0, -1)

  cameraPosition.value = toVec3(nextOrigin.clone().addScaledVector(nextCameraDirection, effectiveCameraDistance))

  if (previousTargetOffset.lengthSq() > 1e-6) {
    lookAtTarget.value = toVec3(nextOrigin.clone().add(previousTargetOffset))
    return
  }

  lookAtTarget.value = { ...value.lookAtTarget }
}

const { readRenderTargetRegionAtClientPoint, disposeRenderTarget } = useRenderTargetRegionAtClientPoint({
  getRenderer: () => tresContextRef.value?.renderer.instance as WebGLRenderer | undefined,
  getScene: () => tresContextRef.value?.scene.value,
  getCamera: () => camera.value,
  getCanvas: () => tresContextRef.value?.renderer.instance.domElement,
})

/*
  * Pinia store definition
  * - Lilia: We highly recommend you gather all the store data definition here
  * - Only this root component (ThreeScene) can directly access pinia store
*/
// TODO: remove the hard-coded pinia store and inject the data from here

/*
  * Handle upward info flow
  * - Sub components emit info => update pinia store
*/
// === OrbitControls ===
// Get camera update => update camera info in pinia
function onOrbitControlsCameraChanged(value: {
  newCameraPosition: Vec3
  newCameraDistance: number
}) {
  const posChanged = Math.abs(cameraPosition.value.x - value.newCameraPosition.x) > 1e-6
    || Math.abs(cameraPosition.value.y - value.newCameraPosition.y) > 1e-6
    || Math.abs(cameraPosition.value.z - value.newCameraPosition.z) > 1e-6
  if (posChanged) {
    cameraPosition.value = value.newCameraPosition
  }
  const distChanged = Math.abs(cameraDistance.value - value.newCameraDistance) > 1e-6
  if (distChanged) {
    cameraDistance.value = value.newCameraDistance
  }
}
const controlsReady = ref(false)
const isCompletingBinding = ref(false)

//  === VRMModel ===
const canvasReady = ref(false)
const modelPhase = ref<ModelPhase>(props.modelSrc ? 'loading' : 'no-model')

function beginSceneBindingCycle(reason: SceneTraceTransactionReason) {
  latestSceneTransactionReason.value = reason
  invalidateBindingRevision()
  resetSceneBindingTransactions()
  emitSceneTransactionTrace('reset', reason)
  beginSceneBindingTransaction()
  emitSceneTransactionTrace('begin', reason)
  setScenePhaseWithTrace('loading', 'vrm:load-start')
}

function beginSceneRebind(reason: SceneTraceTransactionReason = 'subtree-remount') {
  latestSceneTransactionReason.value = reason
  invalidateBindingRevision()

  if (sceneTransactionDepth.value === 0) {
    beginSceneBindingTransaction()
    emitSceneTransactionTrace('begin', reason)
  }

  setScenePhaseWithTrace('loading', 'controls-ref:detached')
}

function setScenePhaseWithTrace(phase: ScenePhase, cause: SceneTracePhaseCause) {
  latestScenePhaseTraceCause.value = cause
  setScenePhase(phase)
}

function commitLastCommittedModelSrc(expectedRevision: number, nextPhase: ScenePhase) {
  if (nextPhase !== 'mounted')
    return

  if (expectedRevision !== bindingRevision.value)
    return

  if (!pendingCommittedModelSrc.value || pendingCommittedModelRevision.value !== expectedRevision)
    return

  if (!activeModelSrc.value || pendingCommittedModelSrc.value !== activeModelSrc.value)
    return

  if (props.modelSrc !== activeModelSrc.value)
    return

  lastCommittedModelSrc.value = pendingCommittedModelSrc.value
  clearPendingCommittedModel()
}

function toSceneLoadTransactionReason(reason: VrmLifecycleReason): SceneTraceTransactionReason {
  switch (reason) {
    case 'initial-load':
    case 'model-reload':
    case 'model-switch':
      return reason
    default:
      return 'unknown'
  }
}

function resolveScenePhaseAfterBinding(): ScenePhase {
  if (!canvasReady.value)
    return 'pending'

  if (!props.modelSrc)
    return 'no-model'

  if (modelPhase.value === 'error')
    return 'error'

  if (modelPhase.value === 'ready')
    return 'mounted'

  return 'loading'
}

async function completeSceneBinding(expectedRevision = bindingRevision.value) {
  if (isCompletingBinding.value)
    return
  isCompletingBinding.value = true

  try {
    setScenePhaseWithTrace('binding', 'binding:start')

    if (pendingSceneBootstrap.value) {
      applySceneBootstrap(pendingSceneBootstrap.value)
      pendingSceneBootstrap.value = undefined
    }

    await nextTick()

    if (expectedRevision !== bindingRevision.value)
      return

    controlsRef.value?.update()

    if (sceneTransactionDepth.value > 0) {
      endSceneBindingTransaction()
      emitSceneTransactionTrace('end', latestSceneTransactionReason.value)
    }

    const nextPhase = resolveScenePhaseAfterBinding()
    setScenePhaseWithTrace(nextPhase, 'binding:complete')
    commitLastCommittedModelSrc(expectedRevision, nextPhase)
  }
  finally {
    isCompletingBinding.value = false
  }
}

function onOrbitControlsReady() {
  controlsReady.value = true

  if (modelPhase.value === 'ready' && scenePhase.value !== 'mounted')
    void completeSceneBinding()
}

const controlEnable = computed(() => {
  return props.enableOrbitControls
    && controlsReady.value
    && modelPhase.value === 'ready'
    && !sceneMutationLocked.value
})
function onVRMModelLoadStart(reason: VrmLifecycleReason) {
  modelPhase.value = 'loading'
  pendingSceneBootstrap.value = undefined
  beginSceneBindingCycle(toSceneLoadTransactionReason(reason))
}

function onVRMSceneBootstrap(value: SceneBootstrap) {
  pendingSceneBootstrap.value = value
}

function onVRMModelLoaded(value: string) {
  activeModelSrc.value = value
  pendingCommittedModelSrc.value = value
  pendingCommittedModelRevision.value = bindingRevision.value
  modelPhase.value = 'ready'
  void completeSceneBinding(bindingRevision.value)
}
function onVRMModelError(error: unknown) {
  invalidateBindingRevision()
  pendingSceneBootstrap.value = undefined
  modelPhase.value = props.modelSrc ? 'error' : 'no-model'
  resetSceneBindingTransactions()
  emitSceneTransactionTrace('reset', 'unknown')
  setScenePhaseWithTrace(props.modelSrc ? 'error' : 'no-model', 'vrm:error')
  emit('error', error)
}

// === sky box ===
const irrSHTex = ref<SphericalHarmonics3 | null>(null)
// Update irrSH for IBL
function onSkyBoxReady(EnvPayload: {
  hdri?: Texture | null
  pmrem?: WebGLRenderTarget | null
  irrSH: SphericalHarmonics3 | null
}) {
  irrSHTex.value = EnvPayload.irrSH || null
}

// === Tres Canvas ===
function onTresReady(context: TresContext) {
  tresContextRef.value = context
  canvasReady.value = true
  emitSceneSubtreeTrace('tresCanvasRef', 'attached')
  setScenePhaseWithTrace(resolveScenePhaseAfterBinding(), 'tres:ready')
}

function onTresRender() {
  if (!isStageThreeRuntimeTraceEnabled())
    return

  const renderer = tresContextRef.value?.renderer.instance
  if (!renderer)
    return

  stageThreeRuntimeTraceContext.emit(stageThreeTraceRenderInfoEvent, {
    drawCalls: renderer.info.render.calls,
    geometries: renderer.info.memory.geometries,
    lines: renderer.info.render.lines,
    points: renderer.info.render.points,
    textures: renderer.info.memory.textures,
    ts: performance.now(),
    triangles: renderer.info.render.triangles,
  })
}

// 动画库是否已初始化完成
// 如果不需要刷新，立即初始化为true
const animationsInitialized = ref(!props.refreshAnimationsOnMount || !props.apiBaseUrl)

onMounted(async () => {
  // 刷新动画库（从服务器获取动画列表）
  if (props.refreshAnimationsOnMount && props.apiBaseUrl) {
    try {
      await animationsStore.refreshLibrary(props.apiBaseUrl)
      const gender = characterGender.value
      console.log('Animation library refreshed:', {
        gender,
        greetCount: animationsStore.getGreetAnimations(gender).length,
        idleCount: animationsStore.getIdleAnimations(gender).length,
      })
    }
    catch (e) {
      // 动画库加载失败不影响模型加载
      console.warn('Failed to refresh animation library:', e)
    }
  }

  // 标记动画库初始化完成
  animationsInitialized.value = true

  if (envSelect.value === 'skyBox') {
    skyBoxEnvRef.value?.reload(skyBoxSrc.value)
  }
})

/**
 * 处理动画完成事件
 * greet完成后切换到idle阶段
 */
function onAnimationComplete(phase: 'greet' | 'idle') {
  if (phase === 'greet') {
    // greet完成后切换到idle
    currentAnimationPhase.value = 'idle'
    animationsStore.currentState = 'idle'

    // 通知父组件
    emit('animationComplete', 'greet')
  }
}

onUnmounted(() => {
  invalidateBindingRevision()
  if (tresContextRef.value)
    emitSceneSubtreeTrace('tresCanvasRef', 'detached')

  canvasReady.value = false
  tresContextRef.value = undefined
  activeModelSrc.value = undefined
  pendingSceneBootstrap.value = undefined
  resetSceneBindingTransactions()
  emitSceneTransactionTrace('reset', 'component-unmount')
  setScenePhaseWithTrace('pending', 'component:unmount')
  disposeRenderTarget()

  // 重置动画状态
  animationsStore.resetState()
})

const effectProps = {
  saturation: 0.3,
  hue: 0,
  blendFunction: BlendFunction.SRC,
}

function applyVrmFrameRuntimeHook() {
  modelRef.value?.setVrmFrameHook(vrmFrameRuntimeHook.value)
}

watch(() => props.modelSrc, (modelSrc) => {
  modelPhase.value = modelSrc ? 'loading' : 'no-model'

  if (!modelSrc) {
    invalidateBindingRevision()
    activeModelSrc.value = undefined
    pendingSceneBootstrap.value = undefined
    resetSceneBindingTransactions()
    emitSceneTransactionTrace('reset', 'no-model')
  }

  setScenePhaseWithTrace(resolveScenePhaseAfterBinding(), 'props:model-src')
}, { immediate: true })

watch(modelRef, (next, prev) => {
  if (!prev && next)
    emitSceneSubtreeTrace('modelRef', 'attached')

  if (next)
    applyVrmFrameRuntimeHook()

  if (prev && !next) {
    emitSceneSubtreeTrace('modelRef', 'detached')
    modelPhase.value = props.modelSrc ? 'loading' : 'no-model'
    setScenePhaseWithTrace(props.modelSrc ? 'loading' : 'no-model', 'model-ref:detached')
  }
}, { flush: 'sync' })

watch(controlsRef, (next, prev) => {
  if (!prev && next)
    emitSceneSubtreeTrace('controlsRef', 'attached')

  if (prev && !next) {
    emitSceneSubtreeTrace('controlsRef', 'detached')
    controlsReady.value = false

    if (props.modelSrc && !!activeModelSrc.value)
      beginSceneRebind()
  }
}, { flush: 'sync' })

watch(dirLightRef, (next, prev) => {
  if (!prev && next)
    emitSceneSubtreeTrace('dirLightRef', 'attached')

  if (prev && !next)
    emitSceneSubtreeTrace('dirLightRef', 'detached')
}, { flush: 'sync' })

// === Directional Light ===
// Directional light setup moved inline, no ready event needed
watch(
  [modelPhase, dirLightRef],
  ([phase, dirLight]) => {
    if (phase !== 'ready' || !dirLight)
      return

    try {
      // setup initial target of directional light
      dirLight.parent?.add(dirLight.target)
      dirLight.target.position.set(
        directionalLightTarget.value.x,
        directionalLightTarget.value.y,
        directionalLightTarget.value.z,
      )
      dirLight.target.updateMatrixWorld()
    }
    catch (error) {
      console.error('[ThreeScene] Failed to setup directional light:', error)
    }
  },
  { immediate: true },
)

watch(scenePhase, (to, from) => {
  emitScenePhaseTrace(latestScenePhaseTraceCause.value, from, to)
}, { immediate: true })

watch(sceneMutationLocked, (locked) => {
  emitSceneMutationLockTrace(locked)
}, { immediate: true })

const resolvedComponentState = computed<'pending' | 'loading' | 'mounted'>(() => {
  if (scenePhase.value === 'pending')
    return 'pending'

  if (scenePhase.value === 'loading' || scenePhase.value === 'binding')
    return 'loading'

  return 'mounted'
})

watch(resolvedComponentState, (to, from) => {
  componentState.value = to
  emitSceneComponentStateTrace(from, to, {
    canvasReady: canvasReady.value,
    controlsReady: controlsReady.value,
    modelPhase: modelPhase.value,
  })
}, { immediate: true })

function updateDirLightTarget(newRotation: { x: number, y: number, z: number }) {
  const light = dirLightRef.value
  if (!light)
    return

  const { x: rx, y: ry, z: rz } = newRotation
  const lightPosition = new Vector3(
    directionalLightPosition.value.x,
    directionalLightPosition.value.y,
    directionalLightPosition.value.z,
  )
  const origin = new Vector3(0, 0, 0)
  const euler = new Euler(
    MathUtils.degToRad(rx),
    MathUtils.degToRad(ry),
    MathUtils.degToRad(rz),
    'XYZ',
  )
  const initialForward = origin.clone().sub(lightPosition).normalize()
  const newForward = initialForward.applyEuler(euler).normalize()
  const distance = lightPosition.distanceTo(origin)
  const target = lightPosition.clone().addScaledVector(newForward, distance)

  light.target.position.copy(target)

  light.target.updateMatrixWorld()

  directionalLightTarget.value = { x: target.x, y: target.y, z: target.z }
}

const getScreenBBox = () => screenRef.value?.containerRef?.getBoundingClientRect() ?? { top: 0, left: 0, width: 500, height: 500 }

watch(directionalLightRotation, (newRotation) => {
  updateDirLightTarget(newRotation)
}, { deep: true })

defineExpose({
  setExpression: (expression: string, intensity = 1, durationMs?: number) => {
    modelRef.value?.setExpression(expression, intensity, durationMs)
  },
  // NOTICE: External runtime hooks are intentionally separate from internal VRM model hooks.
  // This public frame hook is reserved for live pose/tracking input and is forwarded to VRMModel
  // without exposing the internal model/material lifecycle hook pipeline.
  setVrmFrameHook: (hook?: VrmFrameRuntimeHook) => {
    vrmFrameRuntimeHook.value = hook
    applyVrmFrameRuntimeHook()
  },
  canvasElement: () => {
    return tresContextRef.value?.renderer.instance.domElement
  },
  camera: () => camera.value,
  renderer: () => tresContextRef.value?.renderer.instance,
  scene: () => modelRef.value?.scene,
  readRenderTargetRegionAtClientPoint,
  captureFrame: async () => {
    if (!tresContextRef.value)
      return null

    const { renderer, scene } = tresContextRef.value
    renderer.instance.render(scene.value, camera.value)

    return new Promise<Blob | null>((resolve) => {
      renderer.instance.domElement.toBlob(resolve)
    })
  },
})
</script>

<template>
  <Screen ref="screenRef" v-slot="{ width, height }" relative>
    <TresCanvas
      :width="width"
      :height="height"
      :camera="camera"
      :antialias="true"
      :dpr="renderScale"
      :tone-mapping="ACESFilmicToneMapping"
      :tone-mapping-exposure="1"
      :clear-alpha="0"
      @ready="onTresReady"
      @render="onTresRender"
    >
      <OrbitControls
        ref="controlsRef"
        :control-enable="controlEnable"
        :pan-enabled="props.enableOrbitPan"
        :model-size="modelSize"
        :camera-target="modelOrigin"
        @orbit-controls-camera-changed="onOrbitControlsCameraChanged"
        @orbit-controls-ready="onOrbitControlsReady"
      />
      <SkyBox
        v-if="envSelect === 'skyBox'"
        ref="skyBoxEnvRef"
        :sky-box-src="skyBoxSrc"
        :as-background="true"
        @sky-box-ready="onSkyBoxReady"
      />
      <TresHemisphereLight
        v-else
        :color="formatHex(hemisphereSkyColor)"
        :ground-color="formatHex(hemisphereGroundColor)"
        :position="[0, 1, 0]"
        :intensity="hemisphereLightIntensity"
        cast-shadow
      />
      <TresAmbientLight
        :color="formatHex(ambientLightColor)"
        :intensity="ambientLightIntensity"
        cast-shadow
      />
      <TresDirectionalLight
        ref="dirLightRef"
        :color="formatHex(directionalLightColor)"
        :position="[directionalLightPosition.x, directionalLightPosition.y, directionalLightPosition.z]"
        :intensity="directionalLightIntensity"
        cast-shadow
      />
      <Suspense>
        <EffectComposerPmndrs :multisampling="multisampling">
          <HueSaturationPmndrs v-bind="effectProps" />
        </EffectComposerPmndrs>
      </Suspense>
      <VRMModel
        ref="modelRef"
        :current-audio-source="props.currentAudioSource"
        :cursor-position="props.cursorPosition"
        :last-committed-model-src="lastCommittedModelSrc"
        :model-src="props.modelSrc"
        :animation-phase="currentAnimationPhase"
        :greet-animation="greetAnimationUrl"
        :idle-animation="props.idleAnimation"
        :idle-animation-playlist="idleAnimationUrls"
        :paused="props.paused"
        :env-select="envSelect"
        :sky-box-intensity="skyBoxIntensity"
        :npr-irr-s-h="irrSHTex"
        :model-offset="modelOffset"
        :model-rotation-y="modelRotationY"
        :tracking-mode="trackingMode"
        :eye-height="eyeHeight"
        :camera-position="cameraPosition"
        :camera="camera"
        :screen-bounding-box="getScreenBBox"
        @loading-progress="(val: number) => emit('loadModelProgress', val)"
        @load-start="onVRMModelLoadStart"
        @scene-bootstrap="onVRMSceneBootstrap"
        @error="onVRMModelError"
        @loaded="onVRMModelLoaded"
        @animation-complete="onAnimationComplete"
      />
      <TresAxesHelper v-if="props.showAxes" :size="1" />
    </TresCanvas>
  </Screen>
</template>
